import type { SeriesColumnOptions } from "highcharts";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";

import type { IGenderDistribution } from "app/pages/website-analysis/demographics/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type {
    IAgeChartDistributionData,
    IDemographicsAgeData,
    IDemographicsApiTableData,
    IDemographicsTableRecordData,
    IGenderChartDistributionData,
} from "../types";
import type { ICategoryComparison } from "../../categories-context";

export function processGenderDistributionData(genderApiData: IGenderDistribution): IGenderChartDistributionData {
    if (!genderApiData) {
        return null;
    }

    return {
        malePercent: genderApiData.Male,
        femalePercent: genderApiData.Female,
    };
}

export function processAgeDistributionData(ageApiData: IDemographicsAgeData): IAgeChartDistributionData {
    if (!ageApiData) {
        return null;
    }

    return {
        age18To24: ageApiData.Age18To24,
        age25To34: ageApiData.Age25To34,
        age35To44: ageApiData.Age35To44,
        age45To54: ageApiData.Age45To54,
        age55To64: ageApiData.Age55To64,
        age65Plus: ageApiData.Age65,
    };
}

export function processDemographicsTableData(tableApiData: IDemographicsApiTableData): IDemographicsTableRecordData[] {
    const params = proNavigator.getParams();
    const processedData =
        tableApiData?.Data?.map(({ Domain, Favicon, Share, FemalesRatio, MalesRatio, AgeRatios }) => {
            const recDomainUrl = proNavigator.href("digitalsuite_website_websiteperformance", {
                ...params,
                key: Domain,
                isWWW: "*",
            });
            // For some websites, the AgeRatios field is null, so we return the share data only
            if (!AgeRatios) {
                return {
                    Domain: Domain,
                    favicon: Favicon,
                    Share: Share || 0,
                    url: recDomainUrl,
                    hasNoAgeData: true,
                };
            }
            const { Age18To24, Age25To34, Age35To44, Age45To54, Age55To64, Age65 } = AgeRatios;
            return {
                Domain: Domain,
                favicon: Favicon,
                Share: Share || 0,
                // he key name may miss lead, it's in order to support sorting by the MaleShare column
                MaleShare: [MalesRatio, FemalesRatio] as [number, number],
                age18To24: Age18To24 || 0,
                age25To34: Age25To34 || 0,
                age35To44: Age35To44 || 0,
                age45To54: Age45To54 || 0,
                age55To64: Age55To64 || 0,
                age65Plus: Age65 || 0,
                url: recDomainUrl,
            };
        }) ?? [];

    return processedData;
}

export const processAgeDistributionComparisonData = (
    data: Record<string, IAgeChartDistributionData>,
    categories: ICategoryComparison[],
): SimplifiedSeries<SeriesColumnOptions> => {
    if (!data) {
        return null;
    }

    return Object.entries(data).map(([key, value]) => ({
        name: key,
        color: categories.find(({ category }) => category.id === key)?.color || null,
        data: [
            value.age18To24 || 0,
            value.age25To34 || 0,
            value.age35To44 || 0,
            value.age45To54 || 0,
            value.age55To64 || 0,
            value.age65Plus || 0,
        ],
    }));
};
